import { useAppConfigContext } from '@/context/AppConfig';
import { useReportMonitors } from '@/monitoring/index';
import { Grid, styled, Text, Card, Icons, CustomMenu } from '@/theme/index';
import {
  DateUtil,
  rawToValidReportType,
  capitalizeString,
  useScreenSize,
} from '@/utils/index';
import { ClaimedReportCardProps } from 'src/identityHub/interfaces';

const StyledGridItem = styled(Grid)(({ theme }) => ({
  position: 'relative',
  textAlign: 'end',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    top: `${theme.spacing(1)}`,
    right: `${theme.spacing(1)}`,
    maxWidth: '70px',
  },
}));

const TitleText = styled(Text)(({ theme }) => ({
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',

  [theme.breakpoints.down('lg')]: {
    maxWidth: '250px',
  },

  [theme.breakpoints.down('sm')]: {
    maxWidth: '175px',
  },

  [theme.breakpoints.up('lg')]: {
    maxWidth: '300px',
  },
}));
const dateParser = new DateUtil();

/** This component displays claimed report data to the user. The intention is that it can
 * be reused to show data for all report types
 */
export const ClaimedReportCard = ({
  report: { location, updatedAt, createdAt, permalink, name, reportType },
}: ClaimedReportCardProps) => {
  const validReportType = rawToValidReportType(reportType);
  const { isMobile } = useScreenSize();

  const {
    routingUtils: { navigate, viewReportUrl },
    trackEvent,
  } = useAppConfigContext();

  const { toggleReportIsMonitored } = useReportMonitors();

  const parsedDate = (date = '') => {
    return dateParser.parseDateFromString(date, 'D') || '--/--/----';
  };

  const navigateToReport = () => {
    if (validReportType !== null) {
      trackEvent('open_report', 'view', `identity_hub_${validReportType}`);
      navigate(viewReportUrl(validReportType, permalink));
    } else {
      console.error('invalid report type');
    }
  };

  const handleClick = () => {
    trackEvent(
      'unclaim',
      'engagement_click',
      `identity_hub_${validReportType}`
    );
    toggleReportIsMonitored();
  };

  const menuItems = [
    {
      display: 'Remove',
      action: () => {
        handleClick();
      },
    },
  ];

  let cardTitle = name;
  if (reportType === 'detailed_person_report') {
    cardTitle = capitalizeString(name || '');
  }

  return (
    <Card
      sx={{ position: 'relative', padding: '12px', cursor: 'pointer' }}
      variant="outlined"
      className="hover-shadow"
      onClick={navigateToReport}
      data-testid="claimed-report-card"
    >
      <Grid container>
        <Grid container item sm={8}>
          <Grid item sm={12}>
            <TitleText variant="body1" color="primary">
              {cardTitle}
            </TitleText>
          </Grid>
          {location && (
            <Grid item xs={12}>
              <Text variant="caption">{location}</Text>
            </Grid>
          )}
          <Grid item xs={9} sm={9}>
            <Text variant="caption" color="gray">
              Last updated {parsedDate(updatedAt)}
            </Text>{' '}
            {isMobile ? (
              <br />
            ) : (
              <Text variant="caption" color="gray">
                {' '}
                |{' '}
              </Text>
            )}
            <Text variant="caption" color="gray">
              Added {parsedDate(createdAt)}
            </Text>
          </Grid>
        </Grid>
        <StyledGridItem
          container
          item
          sm={4}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CustomMenu
            showIcon
            menuItems={menuItems}
            className="claimed-report__menu-button"
            customIcon={<Icons.Delete fontSize="small" color="primary" />}
            sx={{ padding: '0px' }}
          />
        </StyledGridItem>
      </Grid>
    </Card>
  );
};
